import loadable from '@loadable/component';
import { coreLocationApi, coreLocationLinkingCitiesListApi } from '@cp/entities/location';
import { PageRouteData, coreRouterApi, coreRouterClientWebRoutesMetaByPageApi } from '@cp/entities/clientWebRoutes';
import { coreCatalogItemApi } from '@cp/entities/catalogItem';
import { TranslationGroup } from '@common/model/resource/translationGroup';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { getCityRegionId } from '@common/utils/region';
import { coreCareerApi } from '@cp/entities/career';
import { catalogOptionCoreApi } from '@cp/entities/catalogOption';
import { coreCatalogApi } from '@cp/entities/catalog';
import { resourceTranslationApi } from '@cp/shared/api/core/resource/translation';
import { CLIENT_CATALOG_REVIEW_PAGINATION_SIZE_MAX } from '@common/const/catalog';
import { CATALOG_FEEDBACK_PAGINATION } from '@common/const/catalog/feedback';
import { coreResourceFAQListApi } from '@cp/entities/faq';
import { ThunkDispatch } from 'redux-thunk';
import { coreClientBlocksApi } from '@cp/entities/blocks';
import { coreCatalogFeedbacksApi } from 'src/entities/catalog';
import { getCurrentCityFromStore } from '../shared/utils/city';
import { buildCatalogListRequest } from './catalog/utils/buildCatalogListRequest';
import { buildSubcatalogInversedListRequest, buildSubcatalogListRequest, getIsSubcatalog } from './catalog/lib';
import { HOME_FEEDBACKS_LIST_PARAMETERS } from './home/const';

export const pageIdToRouteData: Partial<{
  [K in ClientWebPage]: PageRouteData<K>;
}> = {
  [ClientWebPage.Home]: {
    component: loadable(() => import('./home')),
    loadData: (route, query, store) => {
      // const currentCity = getCurrentCityFromStore(store);

      // if (!currentCity) {
      //   return Promise.resolve();
      // }

      return Promise.all([
        store.dispatch(
          coreCatalogFeedbacksApi.endpoints.getList.initiate({
            type: HOME_FEEDBACKS_LIST_PARAMETERS.catalogType,
            cityId: HOME_FEEDBACKS_LIST_PARAMETERS.cityId,
            careerId: HOME_FEEDBACKS_LIST_PARAMETERS.careerId,
            page: 1,
            pagination: CATALOG_FEEDBACK_PAGINATION,
          }),
        ),
        store.dispatch(coreLocationApi.endpoints.getCities.initiate({ onlyBigCities: true })),
      ]);
    },
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.Catalog]: {
    component: loadable(() => import('./catalog/index')),
    loadData: (route, query, store, match, context) => {
      const currentCity = getCurrentCityFromStore(store, context.cityAlias);

      const routeProps = route.props;

      if (!routeProps || !currentCity) {
        return Promise.resolve();
      }

      const catalogListRequestParams = buildCatalogListRequest({
        pageProps: routeProps,
        queryParams: query ?? {},
        cityId: currentCity.id,
      });
      const { type, careerId, cityId, filter } = catalogListRequestParams;

      const isSubcatalog = getIsSubcatalog(routeProps);
      const subcatalogListRequest = buildSubcatalogListRequest({ pageProps: routeProps, cityId: currentCity.id });
      const subcatalogInversedListRequest = buildSubcatalogInversedListRequest({ pageProps: routeProps, cityId: currentCity.id });

      return Promise.all(
        [
          store.dispatch(
            coreCatalogApi.endpoints.getItemsCountingForCareerLinking.initiate({
              type: routeProps.catalogType,
              careerId: routeProps.careerId,
              cityId: currentCity.id,
              exceptRouteId: route.routeId as number,
            }),
          ),
          store.dispatch(
            coreCatalogApi.endpoints.getItemsCountingForCityLinking.initiate({
              type: routeProps.catalogType,
              careerId: routeProps.careerId,
              cityId: currentCity.id,
              cityRegionId: Number(getCityRegionId(currentCity)),
              filter,
            }),
          ),
          store.dispatch(
            coreLocationLinkingCitiesListApi.endpoints.getMainLinkingCitiesList.initiate({
              cityId,
              careerId,
              catalogType: type,
              filter,
            }),
          ),
          store.dispatch(catalogOptionCoreApi.endpoints.getOptions.initiate({ careerId, active: true })),
          store.dispatch(resourceTranslationApi.endpoints.getTranslations.initiate({ group: TranslationGroup.Catalog })),
          !isSubcatalog && store.dispatch(coreCatalogApi.endpoints.getItems.initiate(catalogListRequestParams)),

          isSubcatalog && store.dispatch(coreCatalogApi.endpoints.getItems.initiate(subcatalogListRequest)),
          isSubcatalog && store.dispatch(coreCatalogApi.endpoints.getItems.initiate(subcatalogInversedListRequest)),

          store.dispatch(coreCareerApi.endpoints.getCareers.initiate()),
          store.dispatch(coreCatalogApi.endpoints.getItemsCityStats.initiate({ cityId })),

          route.routeId && store.dispatch(coreResourceFAQListApi.endpoints.getFAQList.initiate({ routeId: route.routeId })),
          route.routeId && store.dispatch(coreClientBlocksApi.endpoints.findPageBlocks.initiate({ routeId: route.routeId.toString() })),

          store.dispatch(
            coreLocationApi.endpoints.getCities.initiate({
              regionId: Number(getCityRegionId(currentCity)),
            }),
          ),
          store.dispatch(
            coreCatalogFeedbacksApi.endpoints.getList.initiate({
              type: routeProps.catalogType,
              cityId,
              careerId: routeProps.careerId,
              page: 1,
              pagination: CATALOG_FEEDBACK_PAGINATION,
            }),
          ),
          store.dispatch(
            coreRouterClientWebRoutesMetaByPageApi.endpoints.getClientWebRoutesMetaByPage.initiate({
              pageId: ClientWebPage.Catalog,
              parameters: ['breadcrumb'],
            }),
          ),
          store.dispatch(catalogOptionCoreApi.endpoints.getOptionsDictionary.initiate()),
        ].filter(Boolean),
      );
    },
    hasCitySeparatedContent: true,
  },
  [ClientWebPage.CatalogItem]: {
    component: loadable(() => import('./catalogItem')),
    loadData: (route, query, store, match, context) => {
      const currentCity = getCurrentCityFromStore(store, context.cityAlias);

      if (!route.props || !currentCity || !match?.params.id) {
        return Promise.resolve();
      }

      const catalogListRequestParams = buildCatalogListRequest({
        pageProps: route.props,
        queryParams: query ?? {},
        cityId: currentCity.id,
      });
      const { careerId } = catalogListRequestParams;

      const id = parseInt(match?.params.id);

      return Promise.all([
        store.dispatch(catalogOptionCoreApi.endpoints.getOptionsDictionary.initiate()),
        store.dispatch(catalogOptionCoreApi.endpoints.getOptions.initiate({ careerId })),
        store.dispatch(coreCatalogItemApi.endpoints.getById.initiate({ id })),
        store.dispatch(
          coreCatalogApi.endpoints.getItems.initiate({
            type: catalogListRequestParams.type,
            cityId: catalogListRequestParams.cityId,
            careerId: catalogListRequestParams.careerId,
            pagination: 6,
          }),
        ),
        store.dispatch(coreCareerApi.endpoints.getCareers.initiate()),
        store.dispatch(coreCatalogItemApi.endpoints.getFeedbacks.initiate({ id, pagination: CLIENT_CATALOG_REVIEW_PAGINATION_SIZE_MAX })),
      ]);
    },
    hasCitySeparatedContent: true,
  },
  [ClientWebPage.FAQ]: {
    component: loadable(() => import('./faq')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.Feedbacks]: {
    component: loadable(() => import('./feedbacks')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.LegalInformation]: {
    component: loadable(() => import('./legalInformation')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.PrivacyPolicy]: {
    component: loadable(() => import('./privacyPolicy')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.UserAgreement]: {
    component: loadable(() => import('./userAgreement')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.Support]: {
    component: loadable(() => import('./support')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.PaymentAndRefund]: {
    component: loadable(() => import('./paymentPolicy')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.Contacts]: {
    component: loadable(() => import('./contacts')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.WriteToDirector]: {
    component: loadable(() => import('./writeToDirector')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.AboutUs]: {
    component: loadable(() => import('./aboutUs')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.SignupProvider]: {
    component: loadable(() => import('./signupProvider')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.Profile]: {
    component: loadable(() => import('./aboutMe')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.Favorites]: {
    component: loadable(() => import('./favorites')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.ProfilePhoto]: {
    component: loadable(() => import('./clientProfilePhoto')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.LegacyPaymentFinish]: {
    component: loadable(() => import('./legacyPaymentFinish')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.ProfileMessages]: {
    component: loadable(() => import('./messages')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.Confirmation]: {
    component: loadable(() => import('./confirmation')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.OAuth]: {
    component: loadable(() => import('./oauth')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.Team]: {
    component: loadable(() => import('./team')),
    hasCitySeparatedContent: false,
  },

  [ClientWebPage.ProfileVacancies]: {
    component: loadable(() => import('./profileVacancies')),
    hasCitySeparatedContent: false,
  },

  [ClientWebPage.ProfileSchedule]: {
    component: loadable(() => import('./profileSchedule')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.ProfileRegularPartners]: {
    component: loadable(() => import('./profileRegularPartners')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.ProfileBilling]: {
    component: loadable(() => import('./profileBilling')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.Intro]: {
    component: loadable(() => import('./intro')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.Insurance]: {
    component: loadable(() => import('./insurance')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.Cities]: {
    component: loadable(() => import('./cities')),
    hasCitySeparatedContent: false,
  },
  [ClientWebPage.Sitemap]: {
    component: loadable(() => import('./sitemap')),
    hasCitySeparatedContent: false,
    loadData: async (route, query, store) => {
      const cityIds = (
        await (store.dispatch as ThunkDispatch<unknown, unknown, any>)(
          coreLocationApi.endpoints.getCities.initiate({ onlyBigCities: true }),
        )
      ).data?.map((c) => c.id);

      return Promise.all([
        cityIds?.length
          ? store.dispatch(coreLocationApi.endpoints.getCitiesWithCatalogCounter.initiate({ ids: cityIds }))
          : Promise.resolve(),
        store.dispatch(coreRouterApi.endpoints.getClientWebRoutesMeta.initiate()),
      ]);
    },
  },
};
